/**
 * AudioManager - Web Audio API wrapper for audio-reactive visualizations
 * Handles audio loading, frequency analysis, and real-time audio data extraction
 */

export interface FrequencyData {
  low: number;   // 10-250Hz
  mid: number;   // 250-2000Hz
  high: number;  // 2000Hz+
  overall: number; // Overall amplitude
}

export class AudioManager {
  private audioContext: AudioContext | null = null;
  private audio: HTMLAudioElement | null = null;
  private audioBuffer: AudioBuffer | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private source: MediaElementAudioSourceNode | AudioBufferSourceNode | null = null;
  private frequencyData: FrequencyData = { low: 0, mid: 0, high: 0, overall: 0 };
  private animationFrameId: number | null = null;
  private bufferLength: number = 0;
  
  // Frequency band ranges
  private lowFrequency = 10;
  private midFrequency = 250;
  private highFrequency = 2000;
  private sampleRate = 44100; // Default, will be updated from audio context

  /**
   * Initialize AudioContext
   */
  private async initializeAudioContext(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      await this.audioContext.resume(); // Resume in case it's suspended
    }
    this.sampleRate = this.audioContext.sampleRate;
  }

  /**
   * Load audio from File object
   */
  async loadAudioFromFile(file: File): Promise<void> {
    await this.initializeAudioContext();
    
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          this.audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Load audio from URL
   */
  async loadAudioFromURL(url: string): Promise<void> {
    await this.initializeAudioContext();
    
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized');
    }

    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
  }

  /**
   * Setup audio source and analyser for playback
   */
  setupAudioSource(onEnded?: () => void): void {
    if (!this.audioContext || !this.audioBuffer) {
      throw new Error('Audio not loaded');
    }

    // Clean up existing source if any
    if (this.source instanceof AudioBufferSourceNode) {
      try {
        this.source.stop();
      } catch (e) {
        // Source may already be stopped
      }
    }

    // Create analyser node (reuse if exists)
    if (!this.analyser) {
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      this.bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(this.bufferLength);
    }

    // Create audio source from buffer
    // Audio flows: source -> analyser -> destination
    // The analyser passes audio through transparently while providing frequency data
    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffer;
    source.connect(this.analyser);
    this.analyser.connect(this.audioContext.destination);

    // Handle playback end
    source.onended = () => {
      this.stopAnalysis();
      if (onEnded) {
        onEnded();
      }
    };

    this.source = source;
  }

  /**
   * Play audio
   */
  async play(): Promise<void> {
    if (!this.audioContext || !this.source) {
      throw new Error('Audio source not setup. Call setupAudioSource() first.');
    }

    // Resume context if suspended (required by browsers)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    if (this.source instanceof AudioBufferSourceNode) {
      try {
        this.source.start(0);
      } catch (error) {
        console.error('Failed to start audio source:', error);
        throw error;
      }
    }
  }

  /**
   * Check if audio is loaded
   */
  isLoaded(): boolean {
    return this.audioBuffer !== null;
  }

  /**
   * Stop audio playback
   */
  stop(): void {
    if (this.source instanceof AudioBufferSourceNode) {
      try {
        this.source.stop();
      } catch (e) {
        // Source may already be stopped
      }
      this.source = null;
    }
    this.stopAnalysis();
  }

  /**
   * Start frequency analysis loop
   */
  startAnalysis(): void {
    if (!this.analyser || !this.dataArray) {
      return;
    }

    const updateFrequencyData = () => {
      if (!this.analyser || !this.dataArray) {
        return;
      }

      this.analyser.getByteFrequencyData(this.dataArray);
      this.updateFrequencyBands();
      this.animationFrameId = requestAnimationFrame(updateFrequencyData);
    };

    updateFrequencyData();
  }

  /**
   * Stop frequency analysis
   */
  stopAnalysis(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Calculate frequency bands (low, mid, high)
   */
  private updateFrequencyBands(): void {
    if (!this.dataArray || !this.analyser) {
      return;
    }

    // Calculate frequency ranges
    const lowFreqRangeStart = Math.floor((this.lowFrequency * this.bufferLength) / this.sampleRate);
    const lowFreqRangeEnd = Math.floor((this.midFrequency * this.bufferLength) / this.sampleRate);
    const midFreqRangeEnd = Math.floor((this.highFrequency * this.bufferLength) / this.sampleRate);

    // Calculate averages for each band
    const lowAvg = this.calculateAverage(this.dataArray, lowFreqRangeStart, lowFreqRangeEnd);
    const midAvg = this.calculateAverage(this.dataArray, lowFreqRangeEnd, midFreqRangeEnd);
    const highAvg = this.calculateAverage(this.dataArray, midFreqRangeEnd, this.bufferLength);
    const overallAvg = this.calculateAverage(this.dataArray, 0, this.bufferLength);

    // Normalize to 0-1 range (byte frequency data is 0-255)
    this.frequencyData = {
      low: this.normalizeValue(lowAvg),
      mid: this.normalizeValue(midAvg),
      high: this.normalizeValue(highAvg),
      overall: this.normalizeValue(overallAvg)
    };
  }

  /**
   * Calculate average value in a range
   */
  private calculateAverage(data: Uint8Array, start: number, end: number): number {
    let sum = 0;
    let count = 0;
    for (let i = start; i < end && i < data.length; i++) {
      sum += data[i];
      count++;
    }
    return count > 0 ? sum / count : 0;
  }

  /**
   * Normalize value from 0-255 to 0-1 range
   */
  private normalizeValue(value: number): number {
    return Math.min(value / 255, 1);
  }

  /**
   * Get current frequency data
   */
  getFrequencyData(): FrequencyData {
    return { ...this.frequencyData };
  }

  /**
   * Get audio buffer duration
   */
  getDuration(): number {
    return this.audioBuffer?.duration || 0;
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stop();
    this.stopAnalysis();
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    this.audioBuffer = null;
    this.analyser = null;
    this.dataArray = null;
    this.source = null;
    this.audio = null;
  }
}

export default AudioManager;
